/*
 * Size chart fit scale + desktop description placement (product page).
 *
 * Every size chart on the store is a table of GARMENT measurements laid flat, in cm
 * (Chest = half chest, armpit to armpit; bottoms use "Waist (relaxed, half)").
 * We compare the chart against standard regular-fit averages for that garment type
 * and place the product on a scale: runs small / true to size / runs large.
 *
 * Product metafields can override the result (see snippets/product-size-fit.liquid).
 */
(function () {
  if (window.__sizeChartFitInit) return;
  window.__sizeChartFitInit = true;

  // ---- Parameters -------------------------------------------------------------------------
  // Industry baselines: GARMENT measurements laid flat, in cm, half measurement, per size.
  // Researched Oct 2026 from published mill/brand spec charts of mainstream REGULAR-fit blanks:
  //   Tees:        AS Colour 5001/5026, Gildan 5000/64000/980, Bella+Canvas 3001, Next Level 3600, Comfort Colors 1717
  //   Long sleeve: AS Colour 5029, Gildan 2400/5400, Bella+Canvas 3501, Next Level 3601, Champion CC8C
  //   Polo:        AS Colour Chad 5402, Gildan 8800/64800, Port Authority K500, Biz Crew
  //   Performance: Sport-Tek ST350, Gildan 42000, Under Armour
  //   Fleece tops: AS Colour Supply/Stencil hood, zip & crew, Gildan 18500/18600/18000, Independent SS4500/SS3000,
  //                Champion S700/S600, Lane Seven, Tultex (AU-weighted grade of 3.5cm/size)
  //   Pants:       AS Colour 5917/5921/5932/5920, Gildan 18400/18200/18250, Independent IND20PNT, Champion RW10/P930
  //   Shorts:      AS Colour 5905/5933, Independent IND20SRT, Champion RW26, Bella+Canvas 3724
  // Brand-to-brand spread at M is about +/-2cm (makers quote +/-2.5cm manufacturing tolerance), hence `tol`.
  // Sizes outside each brand's published range (2XS, 3XL+) are extrapolated with the category grade.
  // Length baselines are M values with an estimated +2cm per size; length is only a secondary note.
  const CATEGORIES = {
    tee: {
      noun: 'regular-fit t-shirt', measure: /^(chest|bust)/i, part: 'chest',
      values: { '2XS': 38, XS: 42.5, S: 46.5, M: 51.5, L: 56, XL: 61, '2XL': 65.5, '3XL': 70, '4XL': 74.5, '5XL': 79 },
      tol: 2, large: 5, lengthM: 74
    },
    longsleeve: {
      noun: 'regular-fit long sleeve tee', measure: /^(chest|bust)/i, part: 'chest',
      values: { '2XS': 38, XS: 42.5, S: 46.5, M: 51.5, L: 56, XL: 61, '2XL': 65.5, '3XL': 70, '4XL': 74.5, '5XL': 79 },
      tol: 2, large: 5, lengthM: 74
    },
    polo: {
      noun: 'regular-fit polo', measure: /^(chest|bust)/i, part: 'chest',
      values: { '2XS': 43.5, XS: 47, S: 50.5, M: 54, L: 57.5, XL: 61, '2XL': 65, '3XL': 69, '4XL': 73, '5XL': 77 },
      tol: 2, large: 5, lengthM: 75
    },
    performance: {
      noun: 'regular-fit performance tee', measure: /^(chest|bust)/i, part: 'chest',
      values: { '2XS': 42, XS: 46, S: 49.5, M: 53, L: 57, XL: 61, '2XL': 65.5, '3XL': 70, '4XL': 74.5, '5XL': 79 },
      tol: 2, large: 5, lengthM: 74
    },
    hoodie: {
      noun: 'regular-fit hoodie', measure: /^(chest|bust)/i, part: 'chest',
      values: { '2XS': 45, XS: 48.5, S: 52, M: 55.5, L: 59, XL: 62.5, '2XL': 66, '3XL': 69.5, '4XL': 73, '5XL': 76.5 },
      tol: 2, large: 4.5, lengthM: 73
    },
    zip: {
      noun: 'regular-fit zip hoodie', measure: /^(chest|bust)/i, part: 'chest',
      values: { '2XS': 45, XS: 48.5, S: 52, M: 55.5, L: 59, XL: 62.5, '2XL': 66, '3XL': 69.5, '4XL': 73, '5XL': 76.5 },
      tol: 2, large: 4.5, lengthM: 73
    },
    crew: {
      noun: 'regular-fit crew sweatshirt', measure: /^(chest|bust)/i, part: 'chest',
      values: { '2XS': 45, XS: 48.5, S: 52, M: 55.5, L: 59, XL: 62.5, '2XL': 66, '3XL': 69.5, '4XL': 73, '5XL': 76.5 },
      tol: 2, large: 4.5, lengthM: 73
    },
    pants: {
      noun: 'regular-fit track pant', measure: /^waist/i, part: 'relaxed waist',
      values: { '2XS': 29.5, XS: 32, S: 34.5, M: 37.5, L: 40, XL: 42.5, '2XL': 45.5, '3XL': 48.5, '4XL': 51.5, '5XL': 54.5 },
      tol: 2.5, large: 5, lengthM: null
    },
    shorts: {
      noun: 'regular-fit fleece short', measure: /^waist/i, part: 'relaxed waist',
      values: { '2XS': 31.5, XS: 34, S: 36.5, M: 39.5, L: 42, XL: 45, '2XL': 48, '3XL': 51, '4XL': 54, '5XL': 57 },
      tol: 2.5, large: 5, lengthM: null
    }
  };

  const SIZE_ALIASES = { XXS: '2XS', XXL: '2XL', XXXL: '3XL', XXXXL: '4XL' };
  const SIZE_STEP = { '2XS': -3, XS: -2, S: -1, M: 0, L: 1, XL: 2, '2XL': 3, '3XL': 4, '4XL': 5, '5XL': 6 };
  const LENGTH_GRADE = 2;        // cm per size (estimate)
  const LENGTH_NOTE_AT = 4;      // only mention length when it differs by 4cm+ (ribbing/measuring noise is ~1-2cm)
  const SCALE_RANGE_CM = 8;      // diff that pins the marker to either end
  const OVERRIDE_DIFF = { '-2': -6, '-1': -3, '0': 0, '1': 3.5, '2': 7 }; // metafield fit_scale -> cm

  // ---- Model (pure functions; exposed as window.EASizeFit for testing) ----------------------
  function category(type, title) {
    const all = `${type} ${title}`.toLowerCase();
    if (/\bkids?\b|youth|toddler|baby|infant/.test(all)) return null;
    if (/\bshorts\b/.test(all)) return 'shorts';
    if (/pant|jogger|sweatpant|trackie|legging/.test(all)) return 'pants';
    if (/long sleeve|longsleeve|l\/s/.test(all)) return 'longsleeve';
    if (/\bpolo\b/.test(all)) return 'polo';
    if (/activewear|performance|sports?\b|quick.?dry|cool.?touch|athletic/.test(all)) return 'performance';
    if (/\btee\b|\bt-?shirt/.test(all)) return 'tee'; // \b so "sweatshirt" doesn't match
    if (/hood/.test(all)) return /zip/.test(all) ? 'zip' : 'hoodie';
    if (/crew|sweatshirt|jumper|pullover/.test(all)) return 'crew';
    if (/zip/.test(all)) return 'zip';
    return null;
  }

  // Intended cut from the spec table "Fit" row / title.
  function cutOf(text) {
    const t = String(text).toLowerCase();
    if (/oversi[sz]ed|boxy/.test(t)) return 'oversized';
    if (/relaxed|loose/.test(t)) return 'relaxed';
    if (/slim|fitted|tailored|neat|athletic fit/.test(t)) return 'slim';
    if (/regular|classic|standard/.test(t)) return 'regular';
    return null;
  }

  function normaliseSize(label) {
    const s = String(label).trim().toUpperCase().replace(/\s+/g, '');
    return SIZE_ALIASES[s] || s;
  }

  function num(text) {
    const n = parseFloat(String(text).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }

  // rows: array of arrays of cell text (first row = size header, first column = measurement name).
  function evaluate(rows, catKey) {
    const cat = CATEGORIES[catKey];
    if (!cat || rows.length < 2) return null;
    const sizes = rows[0].map(normaliseSize);
    const mainRow = rows.find(r => cat.measure.test(String(r[0] || '').trim()));
    if (!mainRow) return null;

    const points = [];
    sizes.forEach((size, i) => {
      if (i === 0 || !(size in cat.values)) return;
      const v = num(mainRow[i]);
      if (v !== null) points.push({ size, step: SIZE_STEP[size], diff: v - cat.values[size] });
    });
    if (!points.length) return null;
    // Core sizes S-XL are the most reliable comparison (best-published, least extrapolated).
    const core = points.filter(p => p.step >= -1 && p.step <= 2);
    const used = core.length ? core : points;
    const diff = used.reduce((a, p) => a + p.diff, 0) / used.length;

    let lengthDiff = null;
    const lengthRow = cat.lengthM && rows.find(r => /^length/i.test(String(r[0] || '').trim()));
    if (lengthRow) {
      const l = [];
      sizes.forEach((size, i) => {
        if (i === 0 || !(size in SIZE_STEP)) return;
        const step = SIZE_STEP[size];
        const v = num(lengthRow[i]);
        if (v !== null && step >= -1 && step <= 2) l.push(v - (cat.lengthM + step * LENGTH_GRADE));
      });
      if (l.length) lengthDiff = l.reduce((a, b) => a + b, 0) / l.length;
    }

    const grade = cat.values.L - cat.values.M;
    return { catKey, cat, diff, sizeSteps: diff / grade, lengthDiff };
  }

  // Turns a measured diff + intended cut into what the customer sees.
  function describe(result, cut) {
    const { diff, cat } = result;
    const smallAt = -(cat.tol + 2.5);
    let key, label, advice;
    if (diff <= smallAt) {
      key = 'small'; label = 'Runs small';
      advice = 'Consider going one size up from your usual.';
    } else if (diff < -cat.tol) {
      key = 'slightly-small'; label = 'Runs slightly small';
      advice = 'Size up if you are between sizes or prefer a roomier fit.';
    } else if (diff <= cat.tol) {
      key = 'true'; label = 'True to size';
      advice = cut === 'oversized'
        ? 'Labelled oversized, but it measures close to a standard fit. Take your usual size, or size up for a roomier look.'
        : 'Take your usual size.';
    } else if (diff <= cat.large) {
      key = 'slightly-large';
      if (cut === 'oversized' || cut === 'relaxed') {
        label = 'Relaxed fit';
        advice = 'Roomier than a standard fit by design. Take your usual size for the relaxed look, or size down for a closer fit.';
      } else {
        label = 'Runs slightly large';
        advice = 'Measures a little bigger than a standard fit. Size down if you are between sizes.';
      }
    } else {
      key = 'large';
      if (cut === 'oversized') {
        label = 'Oversized fit';
        advice = 'Cut oversized on purpose. Take your usual size for the oversized look, or size down for a regular fit.';
      } else if (cut === 'relaxed') {
        label = 'Runs large';
        advice = 'Roomier than most relaxed fits, closer to oversized. Size down for a regular fit.';
      } else {
        label = 'Runs large';
        advice = 'Measures noticeably bigger than a standard fit. Consider going one size down.';
      }
    }
    return { key, label, advice };
  }

  function sizeStepsText(result) {
    const steps = Math.round(Math.abs(result.sizeSteps) * 2) / 2; // nearest half size
    const vs = `an average ${result.cat.noun}`;
    if (steps < 0.5) return `Measures in line with ${vs}.`;
    const n = steps === 1 ? 'about 1 size' : `about ${steps} sizes`;
    return `The ${result.cat.part} measures ${n} ${result.diff < 0 ? 'smaller' : 'bigger'} than ${vs}.`;
  }

  function lengthText(result, cut) {
    const d = result.lengthDiff;
    if (d === null || Math.abs(d) < LENGTH_NOTE_AT) return '';
    if (d > 0) return `Longer in the body than average (about ${Math.round(d)}cm).`;
    return `Shorter in the body than average (about ${Math.round(-d)}cm)${cut === 'oversized' ? ', typical of a boxy cut' : ''}.`;
  }

  window.EASizeFit = { CATEGORIES, category, cutOf, evaluate, describe, sizeStepsText, lengthText };

  // ---- DOM helpers ----------------------------------------------------------------------------
  function findSizeTable(scope) {
    return scope.querySelector('.size-chart-content table');
  }

  function tableRows(table) {
    return Array.from(table.rows).map(r => Array.from(r.cells).map(c => (c.dataset.cm ?? c.textContent).trim()));
  }

  // Reads the "Fit" row from the spec table in the description (e.g. "Relaxed oversized, unisex").
  function specFit() {
    const desc = document.getElementById('productDescriptionCollapsible');
    if (!desc) return '';
    for (const row of desc.querySelectorAll('table tr')) {
      const cells = row.cells;
      if (cells.length >= 2 && /^fit$/i.test(cells[0].textContent.trim())) return cells[1].textContent.trim();
    }
    return '';
  }

  function render(el, result, view, opts) {
    const pos = 50 + Math.max(-1, Math.min(1, result.diff / SCALE_RANGE_CM)) * 50;
    const full = el.dataset.fitScaleVariant === 'full';
    const note = opts.note ? `<p class="fit-scale__note">${opts.note}</p>` : '';
    const facts = opts.overridden ? '' : `${sizeStepsText(result)} ${lengthText(result, opts.cut)}`;
    const detail = full ? `<p class="fit-scale__detail">${view.advice} ${facts}</p>` : '';

    el.className = `fit-scale fit-scale--${full ? 'full' : 'compact'} fit-scale--${view.key}`;
    el.innerHTML = `
      <div class="fit-scale__head">
        <span class="fit-scale__title">Fit</span>
        <span class="fit-scale__result">${view.label}</span>
      </div>
      <div class="fit-scale__track" role="img" aria-label="Fit: ${view.label}">
        <span class="fit-scale__tick fit-scale__tick--center" aria-hidden="true"></span>
        <span class="fit-scale__marker" style="left:${pos.toFixed(1)}%" aria-hidden="true"></span>
      </div>
      <div class="fit-scale__labels" aria-hidden="true">
        <span>Runs small</span><span>True to size</span><span>Runs large</span>
      </div>
      ${detail}${note}`;
    el.hidden = false;
  }

  // ---- Size chart lives in the modal only ---------------------------------------------------
  // The product description also contains the size chart (heading + table + tolerance note).
  // Hide that part of the description so the chart only shows once, in the Size Guide.
  function hideChartInDescription() {
    const desc = document.getElementById('productDescriptionCollapsible');
    if (!desc) return '';
    // Accordion layout: the chart has its own "Size and fit" panel, so leave it visible there
    // and just pick up the tolerance note for the modal.
    const sizePanel = desc.querySelector('[data-desc-size]');
    if (sizePanel) {
      const p = Array.from(sizePanel.querySelectorAll('p')).find(el => /measure/i.test(el.textContent));
      return p ? p.textContent.trim() : '';
    }
    const heading = Array.from(desc.querySelectorAll('h1,h2,h3,h4,h5,h6,p > strong'))
      .find(h => /^size (chart|guide)$/i.test(h.textContent.trim()));
    if (!heading) return '';
    const start = heading.tagName === 'STRONG' ? heading.parentElement : heading;
    let note = '';
    let el = start;
    while (el) {
      const next = el.nextElementSibling;
      if (el !== start && /^H[1-6]$/.test(el.tagName)) break;
      if (el.tagName === 'P' && /measure/i.test(el.textContent)) note = el.textContent.trim();
      el.hidden = true;
      el.classList.add('size-chart-in-description');
      el = next;
    }
    return note;
  }

  // ---- cm / inches ----------------------------------------------------------------------------
  const UNIT_KEY = 'ea-size-unit';
  function initUnitToggle(table) {
    const wrap = table.closest('.size-chart-content');
    if (!wrap || wrap.previousElementSibling?.classList.contains('size-chart-toolbar')) return;

    const headerCell = table.rows[0]?.cells[0];
    const headerText = headerCell ? headerCell.textContent : '';
    const cells = [];
    Array.from(table.rows).slice(1).forEach(row => {
      Array.from(row.cells).slice(1).forEach(cell => {
        const cm = parseFloat(cell.textContent);
        if (Number.isFinite(cm)) { cell.dataset.cm = String(cm); cells.push(cell); }
      });
    });
    if (!cells.length) return;

    const toolbar = document.createElement('div');
    toolbar.className = 'size-chart-toolbar';
    toolbar.innerHTML = `
      <span class="size-chart-toolbar__label">Size chart</span>
      <div class="size-unit-toggle" role="group" aria-label="Measurement units">
        <button type="button" data-unit="cm">cm</button>
        <button type="button" data-unit="in">inches</button>
      </div>`;
    wrap.before(toolbar);

    const apply = unit => {
      cells.forEach(cell => {
        const cm = parseFloat(cell.dataset.cm);
        cell.textContent = unit === 'in' ? String(Math.round(cm / 2.54 * 4) / 4) : cell.dataset.cm;
      });
      if (headerCell) headerCell.textContent = headerText.replace(/\((cm|in|inches)\)/i, unit === 'in' ? '(in)' : '(cm)');
      toolbar.querySelectorAll('[data-unit]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.unit === unit)));
      try { localStorage.setItem(UNIT_KEY, unit); } catch (e) { /* storage unavailable */ }
    };
    toolbar.addEventListener('click', e => {
      const btn = e.target.closest('[data-unit]');
      if (btn) apply(btn.dataset.unit);
    });
    let saved = 'cm';
    try { saved = localStorage.getItem(UNIT_KEY) === 'in' ? 'in' : 'cm'; } catch (e) { /* storage unavailable */ }
    apply(saved);
  }

  // ---- Fit scale ------------------------------------------------------------------------------
  function initFitScale() {
    const host = document.querySelector('[data-size-fit]');
    if (!host) return;
    const modal = document.querySelector('modal-dialog[id^="SizeGuideModal-Blank-"]');
    const table = modal && findSizeTable(modal);

    const override = host.dataset.fitOverride;
    const catKey = category(host.dataset.productType || '', host.dataset.productTitle || '');
    let result = null;
    if (override !== undefined && override in OVERRIDE_DIFF) {
      const cat = CATEGORIES[catKey || 'tee'];
      result = { catKey, cat, diff: OVERRIDE_DIFF[override], sizeSteps: 0, lengthDiff: null };
    } else if (table && catKey) {
      result = evaluate(tableRows(table), catKey);
    }
    const cut = cutOf(`${specFit()} ${host.dataset.productTitle || ''}`);

    const descNote = table ? hideChartInDescription() : '';
    if (descNote && !modal.querySelector('.size-chart-note')) {
      const p = document.createElement('p');
      p.className = 'size-chart-note';
      p.textContent = descNote.replace(/,?\s*in (centimetres|centimeters|cm)\b/i, '');
      table.closest('.size-chart-content').after(p);
    }

    // "What this chart is" parameters, shown in the modal above the table.
    if (modal && table && !modal.querySelector('.size-chart-params')) {
      const fit = specFit();
      const params = document.createElement('ul');
      params.className = 'size-chart-params';
      params.innerHTML = `
        <li><strong>Measurements:</strong> garment laid flat (not body measurements)</li>
        <li><strong>Chest:</strong> armpit to armpit, half measurement. Double it for the full chest.</li>
        <li><strong>Length:</strong> highest point of the shoulder down to the hem</li>
        ${fit ? `<li><strong>Cut:</strong> ${fit}</li>` : ''}
        <li><strong>Tip:</strong> lay a shirt that fits you well flat and compare it with the chart</li>`;
      table.closest('.size-chart-content').before(params);

      const fullScale = document.createElement('div');
      fullScale.setAttribute('data-fit-scale', '');
      fullScale.dataset.fitScaleVariant = 'full';
      fullScale.hidden = true;
      params.before(fullScale);
    }

    if (table) initUnitToggle(table);

    if (!result) return;
    const view = describe(result, cut);
    const opts = { note: host.dataset.fitNote || '', overridden: override !== undefined, cut };
    document.querySelectorAll('[data-fit-scale]').forEach(el => render(el, result, view, opts));
  }

  // ---- Size Guide buttons -----------------------------------------------------------------
  function initSizeGuideButtons() {
    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-size-guide-open]');
      if (!btn) return;
      const modal = document.querySelector('modal-dialog[id^="SizeGuideModal-Blank-"]');
      if (!modal) return;
      e.preventDefault();
      if (modal.show) {
        modal.show(btn);
      } else {
        modal.setAttribute('open', '');
        modal.style.display = 'block';
        document.body.classList.add('overflow-hidden');
      }
    });
  }

  // ---- Description placement --------------------------------------------------------------
  // Desktop: description sits under the images (after the Size Guide block).
  // Mobile: leave it where the page's own scripts put it (after the delivery timeline).
  const desktopQuery = window.matchMedia('(min-width: 750px)');
  window.placeProductDescription = function () {
    const description = document.getElementById('productDescriptionCollapsible');
    const mediaWrapper = document.querySelector('.product__media-wrapper');
    if (!description || !mediaWrapper) return;
    if (desktopQuery.matches) {
      const extras = document.getElementById('productMediaExtras');
      if (extras) extras.after(description);
      else mediaWrapper.appendChild(description);
      description.style.display = 'block';
    } else if (mediaWrapper.contains(description)) {
      const timeline = document.getElementById('deliveryTimeline');
      if (timeline && timeline.parentNode) timeline.after(description);
    }
  };
  desktopQuery.addEventListener('change', window.placeProductDescription);

  function init() {
    initSizeGuideButtons();
    initFitScale();
    window.placeProductDescription();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
